// Day 2 - Full Implementation
// Temat: Kontrola nad AI - Zaawansowane techniki promptów
// Duration: 18-22 minutes of continuous activities
// 6 exercises, 42+ steps total for deep engagement

export const day2Content = {
  day_number: 2,
  title: "Kontrola nad AI",
  subtitle: "Precyzja + Iteracja + Zaawansowane techniki",
  duration: "18-22 min",
  exercises: [
    // ═══════════════════════════════════════════════════════════════
    // ĆWICZENIE 1: Iteracja promptu (8 kroków)
    // Cel: Nauczyć użytkownika, że dobry prompt to proces, nie jednorazowe działanie
    // ═══════════════════════════════════════════════════════════════
    {
      id: "d2_ex1",
      title: "Iteracja – klucz do perfekcji",
      icon: "repeat",
      steps: [
        {
          id: "d2_ex1_s1",
          type: "choice",
          title: "Prawda o promptach",
          content: "Profesjonalista pisze idealny prompt za pierwszym razem. Czy to prawda?",
          options: [
            { id: "a", text: "Tak, dobry prompt zawsze działa od razu", correct: false },
            { id: "b", text: "Nie – nawet eksperci iterują i poprawiają prompty wielokrotnie", correct: true },
            { id: "c", text: "Tak, jeśli używasz formuły RZKF", correct: false },
            { id: "d", text: "Zależy od modelu AI", correct: false }
          ],
          shuffle: true,
          softHint: "Pomyśl: czy pisarze piszą idealny tekst za pierwszym razem?",
          successMessage: "Dokładnie! Iteracja to normalna część procesu – nawet dla ekspertów."
        },
        {
          id: "d2_ex1_s2",
          type: "choice",
          title: "Cykl iteracji",
          content: "Jaki jest prawidłowy cykl pracy z promptem?",
          options: [
            { id: "a", text: "Napisz → Sprawdź → Popraw → Powtórz", correct: true },
            { id: "b", text: "Napisz → Kopiuj do Google", correct: false },
            { id: "c", text: "Napisz → Usuń → Zacznij od nowa", correct: false },
            { id: "d", text: "Napisz → Akceptuj cokolwiek dostaniesz", correct: false }
          ],
          shuffle: true,
          softHint: "Iteracja to powtarzalny proces ulepszania.",
          successMessage: "Tak! To jest właściwy workflow – ciągłe doskonalenie."
        },
        {
          id: "d2_ex1_s3",
          type: "reorder",
          title: "Ułóż proces iteracji",
          content: "Ułóż kroki iteracji w prawidłowej kolejności:",
          items: [
            { id: "r1", text: "Napisz pierwszy prompt (wersja 1.0)", order: 1 },
            { id: "r2", text: "Przeczytaj odpowiedź AI uważnie", order: 2 },
            { id: "r3", text: "Zidentyfikuj, co jest nie tak lub brakuje", order: 3 },
            { id: "r4", text: "Dodaj brakujące elementy do promptu", order: 4 },
            { id: "r5", text: "Wyślij ulepszoną wersję (2.0)", order: 5 }
          ],
          softHint: "Najpierw piszesz, potem analizujesz, potem poprawiasz.",
          successMessage: "Perfekcyjnie! To jest profesjonalny workflow."
        },
        {
          id: "d2_ex1_s4",
          type: "choice",
          title: "Analiza odpowiedzi",
          content: "AI napisało: \"Marketing to ważna dziedzina biznesu.\" Co jest nie tak?",
          options: [
            { id: "a", text: "Odpowiedź jest zbyt ogólna – brak konkretów", correct: true },
            { id: "b", text: "Odpowiedź jest idealna", correct: false },
            { id: "c", text: "AI się pomyliło", correct: false },
            { id: "d", text: "Trzeba zmienić model AI", correct: false }
          ],
          shuffle: true,
          softHint: "Czy ta odpowiedź daje Ci konkretną wartość?",
          successMessage: "Tak! Ogólna odpowiedź = potrzeba bardziej precyzyjnego promptu."
        },
        {
          id: "d2_ex1_s5",
          type: "choice",
          title: "Strategia poprawy",
          content: "Jak poprawić prompt, który dał zbyt ogólną odpowiedź?",
          options: [
            { id: "a", text: "Dodać więcej emoji", correct: false },
            { id: "b", text: "Napisać to samo po angielsku", correct: false },
            { id: "c", text: "Dodać konkretne wymagania: liczby, przykłady, format", correct: true },
            { id: "d", text: "Poprosić grzeczniej", correct: false }
          ],
          shuffle: true,
          softHint: "Co sprawia, że odpowiedź jest konkretna?",
          successMessage: "Dokładnie! Konkrety w prompcie = konkrety w odpowiedzi."
        },
        {
          id: "d2_ex1_s6",
          type: "fixPrompt",
          title: "Iteracja w praktyce #1",
          content: "Ten prompt dał zbyt ogólną odpowiedź. Popraw go:",
          originalPrompt: "Napisz o social media dla firm",
          minLength: 80,
          requiredElements: ["social media|media społecznościow", "firm|biznes|przedsiębior", "punkt|lista|przykład|konkret"],
          softHint: "Dodaj: dla jakiej firmy? Jaki aspekt? W jakiej formie? Ile punktów?",
          exampleFix: "Jako ekspert social media, napisz 5 konkretnych strategii na Instagram dla małej kawiarni w Polsce. Dla każdej strategii podaj: nazwę, opis w 2 zdaniach, przykład posta.",
          successMessage: "Świetna iteracja! Teraz AI ma jasne wytyczne."
        },
        {
          id: "d2_ex1_s7",
          type: "fixPrompt",
          title: "Iteracja w praktyce #2",
          content: "Kolejny prompt do poprawy – AI odpowiedziało zbyt krótko:",
          originalPrompt: "Daj mi pomysły na biznes",
          minLength: 90,
          requiredElements: ["pomysł|biznes|startup", "budżet|pieniądz|zł|koszt|inwestycj", "opis|szczegół|krok|plan"],
          softHint: "Dodaj: jaki typ biznesu? Jaki budżet? Ile pomysłów? Jak szczegółowe?",
          exampleFix: "Jako doradca biznesowy, podaj 5 pomysłów na biznes online z budżetem startowym do 5000 zł. Dla każdego pomysłu napisz: nazwa, opis (3 zdania), pierwsze 3 kroki do rozpoczęcia, potencjalny miesięczny przychód.",
          successMessage: "Doskonale! Ten prompt wymusi szczegółową odpowiedź."
        },
        {
          id: "d2_ex1_s8",
          type: "choice",
          title: "Złota zasada iteracji",
          content: "Ile iteracji to \"normalnie\" przy ważnym prompcie?",
          options: [
            { id: "a", text: "Jedna – dobry prompt działa od razu", correct: false },
            { id: "b", text: "2-5 iteracji to standard dla ważnych zadań", correct: true },
            { id: "c", text: "10+ iteracji za każdym razem", correct: false },
            { id: "d", text: "Iteracja to strata czasu", correct: false }
          ],
          shuffle: true,
          softHint: "Pomyśl o równowadze między jakością a czasem.",
          successMessage: "Tak! 2-5 iteracji to zdrowy standard dla ważnych projektów."
        }
      ]
    },

    // ═══════════════════════════════════════════════════════════════
    // ĆWICZENIE 2: Technika Chain-of-Thought (8 kroków)
    // Cel: Nauczyć AI myślenia krok po kroku
    // ═══════════════════════════════════════════════════════════════
    {
      id: "d2_ex2",
      title: "Chain-of-Thought: Myśl krok po kroku",
      icon: "link",
      steps: [
        {
          id: "d2_ex2_s1",
          type: "choice",
          title: "Wprowadzenie do techniki",
          content: "Chain-of-Thought (CoT) to technika, która każe AI:",
          options: [
            { id: "a", text: "Myśleć krok po kroku i pokazywać rozumowanie", correct: true },
            { id: "b", text: "Pisać dłuższe odpowiedzi", correct: false },
            { id: "c", text: "Używać więcej emoji", correct: false },
            { id: "d", text: "Odpowiadać szybciej", correct: false }
          ],
          shuffle: true,
          softHint: "\"Chain\" = łańcuch, \"Thought\" = myśl. Łańcuch myśli...",
          successMessage: "Dokładnie! CoT wymusza logiczne, krokowe rozumowanie."
        },
        {
          id: "d2_ex2_s2",
          type: "choice",
          title: "Kiedy używać CoT?",
          content: "Chain-of-Thought jest szczególnie skuteczny przy:",
          options: [
            { id: "a", text: "Prostych pytaniach typu \"Jaka jest stolica Polski?\"", correct: false },
            { id: "b", text: "Złożonych problemach wymagających analizy i logiki", correct: true },
            { id: "c", text: "Pisaniu wierszy", correct: false },
            { id: "d", text: "Tłumaczeniach", correct: false }
          ],
          shuffle: true,
          softHint: "Kiedy potrzebujesz, żeby AI PRZEMYŚLAŁO problem?",
          successMessage: "Tak! CoT błyszczy przy złożonych, wieloetapowych problemach."
        },
        {
          id: "d2_ex2_s3",
          type: "choice",
          title: "Magiczne frazy CoT",
          content: "Która fraza najlepiej aktywuje myślenie krok po kroku?",
          options: [
            { id: "a", text: "\"Pomyśl krok po kroku, zanim odpowiesz\"", correct: true },
            { id: "b", text: "\"Odpowiedz szybko\"", correct: false },
            { id: "c", text: "\"Napisz krótko\"", correct: false },
            { id: "d", text: "\"Daj mi odpowiedź\"", correct: false }
          ],
          shuffle: true,
          softHint: "Która fraza wymusza pokazanie procesu myślenia?",
          successMessage: "Dokładnie! Ta fraza to klucz do CoT."
        },
        {
          id: "d2_ex2_s4",
          type: "classify",
          title: "Rozpoznaj frazy CoT",
          content: "Przypisz frazy do kategorii:",
          categories: ["Aktywuje CoT", "Nie aktywuje CoT"],
          items: [
            { id: "c1", text: "Pomyśl krok po kroku", category: "Aktywuje CoT" },
            { id: "c2", text: "Przeanalizuj problem etapami", category: "Aktywuje CoT" },
            { id: "c3", text: "Odpowiedz krótko", category: "Nie aktywuje CoT" },
            { id: "c4", text: "Pokaż swoje rozumowanie", category: "Aktywuje CoT" },
            { id: "c5", text: "Daj mi tylko wynik", category: "Nie aktywuje CoT" },
            { id: "c6", text: "Rozważ wszystkie opcje przed decyzją", category: "Aktywuje CoT" }
          ],
          softHint: "CoT = wymuszenie pokazania PROCESU myślenia.",
          successMessage: "Świetnie rozpoznajesz frazy aktywujące głębokie myślenie!"
        },
        {
          id: "d2_ex2_s5",
          type: "choice",
          title: "Porównaj prompty",
          content: "Który prompt da lepszą odpowiedź na złożony problem?",
          options: [
            { id: "a", text: "\"Czy powinienem zmienić pracę?\"", correct: false },
            { id: "b", text: "\"Przeanalizuj krok po kroku, czy powinienem zmienić pracę. Rozważ: finanse, rozwój, work-life balance. Dla każdego aspektu podaj za i przeciw.\"", correct: true },
            { id: "c", text: "\"Praca – zmienić czy nie?\"", correct: false },
            { id: "d", text: "\"Pomóż mi z pracą\"", correct: false }
          ],
          shuffle: true,
          softHint: "Który prompt wymusza strukturalną analizę?",
          successMessage: "Dokładnie! Ten prompt wymusza głęboką, krokową analizę."
        },
        {
          id: "d2_ex2_s6",
          type: "shortPrompt",
          title: "Napisz prompt CoT #1",
          content: "Napisz prompt z Chain-of-Thought do analizy: \"Czy warto inwestować w kryptowaluty?\"",
          placeholder: "Przeanalizuj krok po kroku...",
          minLength: 80,
          requiredElements: ["krok|etap|analiz|rozważ|przemyśl", "krypto|bitcoin|inwestycj", "za i przeciw|plusy|minusy|ryzyko|korzyści"],
          softHint: "Użyj frazy CoT + określ aspekty do analizy + poproś o za i przeciw.",
          successMessage: "Świetny prompt CoT! AI będzie musiało przemyśleć problem dogłębnie."
        },
        {
          id: "d2_ex2_s7",
          type: "shortPrompt",
          title: "Napisz prompt CoT #2",
          content: "Napisz prompt CoT do: \"Planowanie przeprowadzki do innego miasta\"",
          placeholder: "Pomyśl krok po kroku...",
          minLength: 90,
          requiredElements: ["krok|etap|kolejn|plan", "przeprowadzk|miasto", "aspekt|element|rzecz|czynnik|rozważ"],
          softHint: "Poproś o analizę etapami: przygotowanie, logistyka, adaptacja itp.",
          exampleAnswer: "Pomyśl krok po kroku i stwórz plan przeprowadzki do innego miasta. Przeanalizuj etapy: 1) Przygotowanie (co załatwić przed), 2) Logistyka (jak się przeprowadzić), 3) Pierwsze dni (co zrobić na miejscu). Dla każdego etapu podaj 5 konkretnych kroków.",
          successMessage: "Doskonały prompt! CoT jest teraz Twoim narzędziem."
        },
        {
          id: "d2_ex2_s8",
          type: "choice",
          title: "Podsumowanie CoT",
          content: "Główna korzyść z Chain-of-Thought to:",
          options: [
            { id: "a", text: "AI daje bardziej przemyślane, logiczne odpowiedzi", correct: true },
            { id: "b", text: "Odpowiedzi są krótsze", correct: false },
            { id: "c", text: "AI odpowiada szybciej", correct: false },
            { id: "d", text: "Nie trzeba pisać kontekstu", correct: false }
          ],
          shuffle: true,
          softHint: "Co zyskujesz, gdy AI pokazuje swój proces myślenia?",
          successMessage: "Właśnie! CoT = lepsza jakość przez wymuszone rozumowanie."
        }
      ]
    },

    // ═══════════════════════════════════════════════════════════════
    // ĆWICZENIE 3: Few-Shot Learning (7 kroków)
    // Cel: Nauczyć używania przykładów w promptach
    // ═══════════════════════════════════════════════════════════════
    {
      id: "d2_ex3",
      title: "Few-Shot: Ucz AI przez przykłady",
      icon: "copy",
      steps: [
        {
          id: "d2_ex3_s1",
          type: "choice",
          title: "Czym jest Few-Shot?",
          content: "Few-Shot Learning w kontekście promptów oznacza:",
          options: [
            { id: "a", text: "Podanie AI przykładów, jak ma wyglądać odpowiedź", correct: true },
            { id: "b", text: "Zadawanie kilku pytań naraz", correct: false },
            { id: "c", text: "Pisanie bardzo krótkich promptów", correct: false },
            { id: "d", text: "Używanie AI tylko kilka razy", correct: false }
          ],
          shuffle: true,
          softHint: "\"Few-Shot\" = kilka strzałów/przykładów. Co one pokazują?",
          successMessage: "Dokładnie! Przykłady uczą AI, czego oczekujesz."
        },
        {
          id: "d2_ex3_s2",
          type: "choice",
          title: "Dlaczego przykłady działają?",
          content: "Dlaczego podanie przykładu poprawia odpowiedź AI?",
          options: [
            { id: "a", text: "AI widzi konkretny wzorzec do naśladowania", correct: true },
            { id: "b", text: "AI lubi przykłady", correct: false },
            { id: "c", text: "Przykłady są obowiązkowe", correct: false },
            { id: "d", text: "Bez przykładów AI nie działa", correct: false }
          ],
          shuffle: true,
          softHint: "Pomyśl: jak Ty się uczysz? Przez teorię czy przez przykłady?",
          successMessage: "Tak! Wzorzec jest wart więcej niż opis."
        },
        {
          id: "d2_ex3_s3",
          type: "choice",
          title: "Struktura Few-Shot",
          content: "Jak wygląda prompt z Few-Shot?",
          options: [
            { id: "a", text: "Instrukcja + Przykład(y) + Twoje zadanie", correct: true },
            { id: "b", text: "Tylko przykłady bez instrukcji", correct: false },
            { id: "c", text: "Tylko instrukcja bez przykładów", correct: false },
            { id: "d", text: "Losowa kolejność", correct: false }
          ],
          shuffle: true,
          softHint: "Najpierw mówisz CO, potem pokazujesz JAK, potem dajesz zadanie.",
          successMessage: "Dokładnie! To jest struktura skutecznego Few-Shot."
        },
        {
          id: "d2_ex3_s4",
          type: "reorder",
          title: "Ułóż prompt Few-Shot",
          content: "Ułóż elementy promptu Few-Shot w prawidłowej kolejności:",
          items: [
            { id: "r1", text: "Instrukcja: \"Zamień zdania na pytania\"", order: 1 },
            { id: "r2", text: "Przykład 1: \"Pada deszcz\" → \"Czy pada deszcz?\"", order: 2 },
            { id: "r3", text: "Przykład 2: \"Kot śpi\" → \"Czy kot śpi?\"", order: 3 },
            { id: "r4", text: "Twoje zadanie: \"Samochód jedzie\"", order: 4 }
          ],
          softHint: "Instrukcja → Przykłady → Zadanie do wykonania.",
          successMessage: "Perfekcyjnie! To jest wzorcowa struktura Few-Shot."
        },
        {
          id: "d2_ex3_s5",
          type: "choice",
          title: "Ile przykładów?",
          content: "Ile przykładów jest optymalnych w Few-Shot?",
          options: [
            { id: "a", text: "1 przykład zawsze wystarczy", correct: false },
            { id: "b", text: "2-3 przykłady to dobry standard", correct: true },
            { id: "c", text: "Minimum 10 przykładów", correct: false },
            { id: "d", text: "Im więcej, tym lepiej – 50+", correct: false }
          ],
          shuffle: true,
          softHint: "Chodzi o równowagę – wystarczająco dużo do zrozumienia wzorca.",
          successMessage: "Tak! 2-3 przykłady to sweet spot dla większości zadań."
        },
        {
          id: "d2_ex3_s6",
          type: "shortPrompt",
          title: "Stwórz prompt Few-Shot",
          content: "Napisz prompt Few-Shot do generowania haseł reklamowych. Daj 2 przykłady i zadanie.",
          placeholder: "Zadanie: Twórz hasła reklamowe.\n\nPrzykład 1: Produkt: Kawa → Hasło: \"Pobudź swój dzień\"\nPrzykład 2: ...",
          minLength: 120,
          requiredElements: ["przykład|example", "hasło|slogan|reklam", "produkt|marka|→"],
          softHint: "Format: Instrukcja + 2 przykłady (Produkt → Hasło) + Twoje zadanie",
          successMessage: "Świetny Few-Shot! AI teraz dokładnie wie, jakiego formatu oczekujesz."
        },
        {
          id: "d2_ex3_s7",
          type: "choice",
          title: "Kiedy Few-Shot jest najlepszy?",
          content: "Few-Shot Learning sprawdza się najlepiej gdy:",
          options: [
            { id: "a", text: "Potrzebujesz odpowiedzi w bardzo specyficznym formacie lub stylu", correct: true },
            { id: "b", text: "Masz proste pytanie z jedną odpowiedzią", correct: false },
            { id: "c", text: "Nie wiesz, czego chcesz", correct: false },
            { id: "d", text: "Zawsze, przy każdym prompcie", correct: false }
          ],
          shuffle: true,
          softHint: "Przykłady pokazują FORMAT i STYL. Kiedy to jest kluczowe?",
          successMessage: "Dokładnie! Few-Shot to Twoja broń na precyzyjne formaty."
        }
      ]
    },

    // ═══════════════════════════════════════════════════════════════
    // ĆWICZENIE 4: Negative Prompting (7 kroków)
    // Cel: Nauczyć, czego AI ma NIE robić
    // ═══════════════════════════════════════════════════════════════
    {
      id: "d2_ex4",
      title: "Negative Prompting: Czego NIE robić",
      icon: "x-circle",
      steps: [
        {
          id: "d2_ex4_s1",
          type: "choice",
          title: "Czym jest Negative Prompting?",
          content: "Negative Prompting to technika, w której:",
          options: [
            { id: "a", text: "Mówisz AI, czego ma UNIKAĆ w odpowiedzi", correct: true },
            { id: "b", text: "Piszesz negatywne opinie", correct: false },
            { id: "c", text: "Krytykujesz AI", correct: false },
            { id: "d", text: "Używasz słów typu \"nie\" na początku", correct: false }
          ],
          shuffle: true,
          softHint: "\"Negative\" = co AI ma wykluczyć, ominąć, nie robić.",
          successMessage: "Tak! Mówisz AI, czego ma NIE robić – to równie ważne jak mówienie, co robić."
        },
        {
          id: "d2_ex4_s2",
          type: "choice",
          title: "Dlaczego to działa?",
          content: "Dlaczego warto powiedzieć AI, czego ma unikać?",
          options: [
            { id: "a", text: "Bo AI często dodaje rzeczy, których nie chcesz", correct: true },
            { id: "b", text: "Bo AI lubi negatywne instrukcje", correct: false },
            { id: "c", text: "Bo to obowiązkowe", correct: false },
            { id: "d", text: "Bo AI odpowiada szybciej", correct: false }
          ],
          shuffle: true,
          softHint: "Pomyśl o sytuacji, gdy AI dało odpowiedź z niechcianymi elementami.",
          successMessage: "Dokładnie! Wykluczenia pomagają uniknąć niechcianych elementów."
        },
        {
          id: "d2_ex4_s3",
          type: "classify",
          title: "Rozpoznaj wykluczenia",
          content: "Przypisz frazy do odpowiedniej kategorii:",
          categories: ["Pozytywna instrukcja", "Negative Prompting"],
          items: [
            { id: "c1", text: "Napisz profesjonalnie", category: "Pozytywna instrukcja" },
            { id: "c2", text: "Nie używaj żargonu", category: "Negative Prompting" },
            { id: "c3", text: "Unikaj zbyt długich zdań", category: "Negative Prompting" },
            { id: "c4", text: "Użyj przykładów", category: "Pozytywna instrukcja" },
            { id: "c5", text: "Nie zaczynaj od \"Oczywiście\"", category: "Negative Prompting" },
            { id: "c6", text: "Bądź konkretny", category: "Pozytywna instrukcja" }
          ],
          softHint: "Negative = co NIE robić. Pozytywna = co robić.",
          successMessage: "Świetnie rozpoznajesz różnicę między instrukcjami!"
        },
        {
          id: "d2_ex4_s4",
          type: "choice",
          title: "Typowe wykluczenia",
          content: "Które wykluczenie jest przydatne przy tekstach biznesowych?",
          options: [
            { id: "a", text: "\"Nie używaj emotikon i nieformalnego języka\"", correct: true },
            { id: "b", text: "\"Nie pisz po polsku\"", correct: false },
            { id: "c", text: "\"Nie odpowiadaj\"", correct: false },
            { id: "d", text: "\"Nie używaj słów\"", correct: false }
          ],
          shuffle: true,
          softHint: "Co często psuje profesjonalny ton tekstu?",
          successMessage: "Tak! To wykluczenie utrzymuje profesjonalny ton."
        },
        {
          id: "d2_ex4_s5",
          type: "fill",
          title: "Dokończ prompt",
          content: "Uzupełnij negative prompting dla opisu produktu:",
          template: "Napisz opis produktu. ______ marketingowych przesadzeń i pustych fraz typu \"najlepszy na świecie\".",
          correctAnswers: ["Unikaj", "Nie używaj", "Bez", "Pomiń", "Nie stosuj"],
          minLength: 4,
          softHint: "Słowo, które mówi AI, czego NIE robić.",
          successMessage: "Dokładnie! Takie wykluczenie daje autentyczne opisy."
        },
        {
          id: "d2_ex4_s6",
          type: "shortPrompt",
          title: "Napisz prompt z wykluczeniami",
          content: "Napisz prompt na email do klienta z minimum 3 wykluczeniami (czego AI ma NIE robić):",
          placeholder: "Napisz email do klienta... Nie... Unikaj... Bez...",
          minLength: 100,
          requiredElements: ["email", "nie |unikaj|bez |pomiń"],
          softHint: "Dodaj wykluczenia: nie używaj żargonu, nie bądź zbyt formalny, unikaj długich zdań itp.",
          successMessage: "Świetny prompt z wykluczeniami! Dużo większa kontrola nad wynikiem."
        },
        {
          id: "d2_ex4_s7",
          type: "choice",
          title: "Kombinacja technik",
          content: "Najlepsze prompty łączą:",
          options: [
            { id: "a", text: "Pozytywne instrukcje (co robić) + Negative Prompting (czego nie robić)", correct: true },
            { id: "b", text: "Tylko pozytywne instrukcje", correct: false },
            { id: "c", text: "Tylko wykluczenia", correct: false },
            { id: "d", text: "Ani jedno, ani drugie", correct: false }
          ],
          shuffle: true,
          softHint: "Pomyśl o pełnej kontroli – musisz wiedzieć co TAK i co NIE.",
          successMessage: "Perfekcyjnie! Kombinacja daje maksymalną kontrolę."
        }
      ]
    },

    // ═══════════════════════════════════════════════════════════════
    // ĆWICZENIE 5: Mega-prompt praktyczny (7 kroków)
    // Cel: Połączenie wszystkich technik w jeden potężny prompt
    // ═══════════════════════════════════════════════════════════════
    {
      id: "d2_ex5",
      title: "Mega-Prompt: Połącz wszystko",
      icon: "layers",
      steps: [
        {
          id: "d2_ex5_s1",
          type: "choice",
          title: "Czym jest Mega-Prompt?",
          content: "Mega-Prompt to:",
          options: [
            { id: "a", text: "Prompt łączący wiele technik: RZKF + CoT + Few-Shot + wykluczenia", correct: true },
            { id: "b", text: "Bardzo długi prompt bez struktury", correct: false },
            { id: "c", text: "Prompt napisany WIELKIMI LITERAMI", correct: false },
            { id: "d", text: "Prompt z wieloma emoji", correct: false }
          ],
          shuffle: true,
          softHint: "\"Mega\" = połączenie wielu technik w jeden potężny prompt.",
          successMessage: "Tak! Mega-Prompt to Twoja najpotężniejsza broń."
        },
        {
          id: "d2_ex5_s2",
          type: "reorder",
          title: "Struktura Mega-Promptu",
          content: "Ułóż elementy Mega-Promptu w zalecanej kolejności:",
          items: [
            { id: "r1", text: "ROLA – kim jest AI", order: 1 },
            { id: "r2", text: "ZADANIE – co zrobić", order: 2 },
            { id: "r3", text: "KONTEKST – dla kogo, dlaczego", order: 3 },
            { id: "r4", text: "PRZYKŁADY (Few-Shot) – wzorce", order: 4 },
            { id: "r5", text: "WYKLUCZENIA – czego nie robić", order: 5 },
            { id: "r6", text: "FORMAT – jak ma wyglądać", order: 6 }
          ],
          softHint: "RZKF + przykłady + wykluczenia + format końcowy.",
          successMessage: "To jest struktura mistrzowskiego promptu!"
        },
        {
          id: "d2_ex5_s3",
          type: "choice",
          title: "Kiedy używać Mega-Promptu?",
          content: "Mega-Prompt jest najlepszy dla:",
          options: [
            { id: "a", text: "Prostych pytań typu \"Która godzina?\"", correct: false },
            { id: "b", text: "Ważnych, złożonych zadań wymagających precyzji", correct: true },
            { id: "c", text: "Każdego pytania bez wyjątku", correct: false },
            { id: "d", text: "Tylko dla programistów", correct: false }
          ],
          shuffle: true,
          softHint: "Mega-Prompt wymaga więcej pracy. Kiedy ta inwestycja się opłaca?",
          successMessage: "Dokładnie! Mega-Prompt to heavy artillery dla ważnych zadań."
        },
        {
          id: "d2_ex5_s4",
          type: "classify",
          title: "Przypisz elementy",
          content: "Przypisz fragmenty Mega-Promptu do kategorii:",
          categories: ["ROLA", "ZADANIE", "PRZYKŁAD", "WYKLUCZENIE"],
          items: [
            { id: "c1", text: "Jesteś ekspertem copywritingu", category: "ROLA" },
            { id: "c2", text: "Napisz 5 nagłówków reklamowych", category: "ZADANIE" },
            { id: "c3", text: "Np. \"Kawa\" → \"Pobudź swój dzień\"", category: "PRZYKŁAD" },
            { id: "c4", text: "Nie używaj wykrzykników", category: "WYKLUCZENIE" }
          ],
          softHint: "Każdy element ma swoją funkcję w Mega-Prompcie.",
          successMessage: "Świetnie rozpoznajesz strukturę Mega-Promptu!"
        },
        {
          id: "d2_ex5_s5",
          type: "shortPrompt",
          title: "Napisz ROLĘ + ZADANIE + KONTEKST",
          content: "Napisz pierwszą część Mega-Promptu dla: \"Strategia content marketingu dla startupu\"",
          placeholder: "Jesteś... Stwórz... dla...",
          minLength: 80,
          requiredElements: ["jesteś", "content|marketing|strategi", "startup|firm|biznes"],
          softHint: "ROLA (ekspert) + ZADANIE (stwórz strategię) + KONTEKST (dla jakiego startupu).",
          successMessage: "Świetny początek Mega-Promptu!"
        },
        {
          id: "d2_ex5_s6",
          type: "shortPrompt",
          title: "Dodaj PRZYKŁAD + WYKLUCZENIA + FORMAT",
          content: "Dokończ Mega-Prompt – dodaj przykład formatu, wykluczenia i format końcowy:",
          placeholder: "Przykład: ...\nNie używaj...\nFormat: ...",
          minLength: 100,
          requiredElements: ["przykład|np|wzór", "nie |unikaj|bez ", "format|struktur|lista|punkt"],
          softHint: "Pokaż wzór odpowiedzi + napisz czego unikać + określ format.",
          successMessage: "Kompletny Mega-Prompt! To jest profesjonalny poziom."
        },
        {
          id: "d2_ex5_s7",
          type: "choice",
          title: "Podsumowanie Mega-Promptu",
          content: "Główna korzyść z Mega-Promptu to:",
          options: [
            { id: "a", text: "Maksymalna kontrola nad odpowiedzią AI", correct: true },
            { id: "b", text: "Szybsze pisanie", correct: false },
            { id: "c", text: "Krótsze odpowiedzi", correct: false },
            { id: "d", text: "AI odpowiada natychmiast", correct: false }
          ],
          shuffle: true,
          softHint: "Co zyskujesz, łącząc wszystkie techniki?",
          successMessage: "Właśnie! Mega-Prompt = maksymalna precyzja i kontrola."
        }
      ]
    },

    // ═══════════════════════════════════════════════════════════════
    // ĆWICZENIE 6: Misja końcowa + Quiz (7 kroków)
    // Cel: Sprawdzenie wiedzy i stworzenie własnego zaawansowanego promptu
    // ═══════════════════════════════════════════════════════════════
    {
      id: "d2_ex6",
      title: "Misja końcowa Dnia 2",
      icon: "trophy",
      steps: [
        {
          id: "d2_ex6_s1",
          type: "choice",
          title: "Quiz 1/4: Iteracja",
          content: "Ile iteracji promptu to zdrowy standard dla ważnego zadania?",
          options: [
            { id: "a", text: "0 – dobry prompt działa od razu", correct: false },
            { id: "b", text: "2-5 iteracji", correct: true },
            { id: "c", text: "Minimum 20 iteracji", correct: false },
            { id: "d", text: "Iteracja jest błędem", correct: false }
          ],
          shuffle: true,
          softHint: "Przypomnij sobie ćwiczenie o iteracji.",
          successMessage: "Tak! 2-5 iteracji to profesjonalny standard."
        },
        {
          id: "d2_ex6_s2",
          type: "choice",
          title: "Quiz 2/4: Chain-of-Thought",
          content: "Która fraza aktywuje Chain-of-Thought?",
          options: [
            { id: "a", text: "\"Odpowiedz krótko\"", correct: false },
            { id: "b", text: "\"Pomyśl krok po kroku\"", correct: true },
            { id: "c", text: "\"Napisz szybko\"", correct: false },
            { id: "d", text: "\"Daj mi tylko wynik\"", correct: false }
          ],
          shuffle: true,
          softHint: "CoT wymusza pokazanie procesu myślenia.",
          successMessage: "Dokładnie! To klucz do głębokiego rozumowania."
        },
        {
          id: "d2_ex6_s3",
          type: "choice",
          title: "Quiz 3/4: Few-Shot",
          content: "Few-Shot Learning polega na:",
          options: [
            { id: "a", text: "Podaniu AI przykładów wzorcowych odpowiedzi", correct: true },
            { id: "b", text: "Zadawaniu kilku pytań naraz", correct: false },
            { id: "c", text: "Krótkim pisaniu", correct: false },
            { id: "d", text: "Używaniu AI tylko kilka razy dziennie", correct: false }
          ],
          shuffle: true,
          softHint: "Few-Shot = kilka przykładów jako wzorzec.",
          successMessage: "Tak! Przykłady uczą AI oczekiwanego formatu."
        },
        {
          id: "d2_ex6_s4",
          type: "choice",
          title: "Quiz 4/4: Negative Prompting",
          content: "Negative Prompting służy do:",
          options: [
            { id: "a", text: "Pisania negatywnych opinii", correct: false },
            { id: "b", text: "Mówienia AI, czego ma UNIKAĆ", correct: true },
            { id: "c", text: "Krytykowania AI", correct: false },
            { id: "d", text: "Usuwania odpowiedzi", correct: false }
          ],
          shuffle: true,
          softHint: "\"Negative\" = wykluczenia, czego NIE robić.",
          successMessage: "Perfekcyjnie! Wykluczenia dają większą kontrolę."
        },
        {
          id: "d2_ex6_s5",
          type: "buildPrompt",
          title: "Stwórz Mega-Prompt",
          content: "Połącz wszystko: stwórz Mega-Prompt do analizy konkurencji dla Twojej firmy:",
          sections: ["ROLA", "ZADANIE", "KONTEKST", "PRZYKŁAD/WZÓR", "WYKLUCZENIA", "FORMAT"],
          minTotalLength: 150,
          softHint: "Użyj wszystkich technik: RZKF + przykład + wykluczenia + CoT!",
          successMessage: "🎉 MISTRZOWSKI MEGA-PROMPT! To jest profesjonalny poziom!"
        },
        {
          id: "d2_ex6_s6",
          type: "choice",
          title: "Refleksja",
          content: "Która technika z Dnia 2 jest dla Ciebie najbardziej przydatna?",
          options: [
            { id: "a", text: "Iteracja – ciągłe ulepszanie promptów", correct: true, isChoice: true },
            { id: "b", text: "Chain-of-Thought – myślenie krok po kroku", correct: true, isChoice: true },
            { id: "c", text: "Few-Shot – uczenie przez przykłady", correct: true, isChoice: true },
            { id: "d", text: "Negative Prompting – wykluczenia", correct: true, isChoice: true }
          ],
          shuffle: false,
          softHint: "Która technika najbardziej pasuje do Twoich potrzeb?",
          successMessage: "Świetny wybór! Używaj tej techniki jako pierwszej."
        },
        {
          id: "d2_ex6_s7",
          type: "choice",
          title: "🎉 Gratulacje!",
          content: "Ukończyłeś Dzień 2! Co dalej?",
          options: [
            { id: "a", text: "Zobacz podsumowanie i zakończ", correct: true, isChoice: true },
            { id: "b", text: "Przejdź do Dnia 3 jutro", correct: true, isChoice: true }
          ],
          shuffle: false,
          softHint: "Wybierz swoją ścieżkę!",
          successMessage: "🏆 Dzień 2 ukończony! Opanowałeś zaawansowane techniki promptów. Jutro Dzień 3 – zastosowania praktyczne!"
        }
      ]
    }
  ]
};

export default day2Content;
